import {
  Banner,
  BlockStack,
  Button,
  Card,
  DataTable,
  InlineStack,
  Text,
} from "@shopify/polaris";

import type { StockyCopyResultDomain } from "../../api/stocky_copy_jobs";
import { t } from "../../lib/i18n";
import { STOCKY_COPY_RESULT_DOMAIN_LABEL } from "../MigrationWizard/stockyCopyProfile";

const MANUAL_COPY_SOURCE: Record<StockyCopyResultDomain, string> = {
  suppliers:
    "Stocky公式CSVなし。手元で作る suppliers.csv（仕入先名、会社名、担当者、メール、電話）で補います。",
  purchase_tax_types:
    "Stocky公式CSVなし。手元で作る purchase_tax_types.csv（Tax name、Rate %、Purpose）で補います。",
  variant_suppliers:
    "Stocky公式CSVなし。手元で作る supplier_links.csv（SKU、仕入先名、単価、入数）で補います。",
  purchase_orders:
    "保存済みの purchase_orders.csv と、各発注書の purchase_order_<PO番号>.csv を選びます。",
  stock_adjustments:
    "保存済みの stock_adjustments.csv を選びます。",
  reorder_policies:
    "保存済みの low_stock_csv.csv を選びます。",
  supplier_lead_times:
    "Low Stock・発注点と同じ low_stock_csv.csv の Vendor・Lead Time を使います。",
  minimum_order_quantities:
    "終了前に保存した Order limits の表がある場合だけ、Vendorごとに貼り付けます。",
  stocktakes:
    "保存済みの download_csv.csv を、棚卸し1件につき1ファイル選びます。",
  product_catalog:
    "保存済みファイルからは完全取得できません。現在のShopify商品はダッシュボードの「今すぐ同期」で更新します。",
  forecasting_defaults:
    "保存済みファイルからは取得できません。",
  report_originals:
    "Stocky公式エクスポートまたは終了前に保存した、Stock on hand・Average costなどの対応CSVを選びます。",
};

/**
 * 「全部」の意味をマーチャントが操作へ落とせる形で示す。
 *
 * Stocky 側の総件数や選択中フィルターは UreyukiBox から読めないため、偽の
 * 自動完了バッジは出さない。その代わり、手動で保存できる範囲、ファイルごとの
 * 限界、最後に照合する単位を同じ場所に置く。
 */
export function StockyMigrationRouteSummary() {
  const visibleDomains = Object.keys(
    STOCKY_COPY_RESULT_DOMAIN_LABEL,
  ) as StockyCopyResultDomain[];
  const rows = visibleDomains.map((domain) => [
    t(STOCKY_COPY_RESULT_DOMAIN_LABEL[domain]),
    t(MANUAL_COPY_SOURCE[domain]),
  ]);

  return (
    <Card>
      <BlockStack gap="400">
        <BlockStack gap="100">
          <Text as="h2" variant="headingLg">
            {t("取り込める12項目")}
          </Text>
          <Text as="p" variant="bodyMd">
            {t(
              "左列はUreyukiBoxでの保存先、右列は使える保存済みファイル・公式エクスポートです。ファイルにない項目は補完しません。",
            )}
          </Text>
        </BlockStack>

        <Banner
          tone="warning"
          title={t("保存済みファイルだけでは完全取得できない項目があります")}
        >
          <Text as="p">
            {t(
              "商品・バリエーションはShopify同期を正とし、需要予測設定は保存済みファイルから取得できません。仕入先・仕入用税区分・商品と仕入先の紐付けは、公式ファイルがない場合に手元で作るCSVで補います。",
            )}
          </Text>
        </Banner>

        <DataTable
          columnContentTypes={["text", "text"]}
          headings={[t("取込先"), t("使う保存済みファイル・公式エクスポート")]}
          rows={rows}
        />

        <InlineStack gap="200" wrap>
          <Button url="#stocky-required-imports" variant="primary">
            {t("入力欄へ進む")}
          </Button>
          <Button url="#stocky-saved-sources">
            {t("保存済み原本を確認する")}
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}
